import { CheckIcon } from '@heroicons/react/16/solid';
import { ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../../../providers/UserProvider/User.context.ts';
import { eUserRole } from '../../../../../../../../providers/UserProvider/User.enums.ts';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import {
  EventType,
  MessageErrorType,
  MessageState,
} from '../../../../../../Chat.enum.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { ChatService } from '../../../../../../Chat.service.ts';
import { tChatEvent, tUpdateEventData } from '../../../../../../Chat.types.ts';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
  lookupOutboundRefusalByCode,
} from '../../../../../../../../utils/ErrorDirectory.ts';
import { tMessageBubbleMenuHook } from './MessageBubbleMenu.types.ts';
import { useTranslation } from 'react-i18next';

export default function useMessageBubbleMenu(): tMessageBubbleMenuHook {
  const { addAlert } = useToastContext();
  const { currentUserId, currentUser, currentUserRole } = useUserContext();
  const {
    selectedChat,
    favoriteMessages,
    setFavoriteMessages,
    chatEvents,
    setChatEvents,
  } = useChatContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const [loading, setLoading] = useState<boolean>(false);

  // `errors/errors` holds the copy for a refused send (see outboundRefusalLine);
  // `chat/chat` stays first so every other key here resolves without an `ns`.
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  const isWapi = selectedChat?.integration === eChannelType.WHATSAPP_API;

  // Chat codes 1016-1018: the server refused the send before it reached any
  // provider. Only 1016 says the plan is paused; the other two can happen to a
  // workspace that is not paused, so they get the neutral line.
  const outboundRefusalLine = (error: string) => {
    const refusal = lookupOutboundRefusalByCode(error);
    return refusal ? t(refusal.bubbleKey, { ns: 'errors/errors' }) : '';
  };

  // BUG-587 (R7): four WAPI conditions have a remedy only an admin-side user can
  // execute (Business Manager, Meta support). Branch the copy so a seller is told
  // who to ask instead of being handed an action they cannot perform. Mirrors
  // MessageBar.content.tsx:116-119. `currentUserRole` defaults to SELLER before
  // workspace info resolves, which the seller-side default handles safely — and
  // OWNER must sit on the admin side, or the workspace owner is told to "ask your
  // administrator" about their own account.
  const isAdminSideUser =
    currentUserRole === eUserRole.OWNER || currentUserRole === eUserRole.ADMIN;
  const roleVariant = (key: string) =>
    t(`${key}.${isAdminSideUser ? 'admin' : 'seller'}`);

  function showCheckStatus(status: MessageState, error?: string) {
    switch (status) {
      // INT-898: SENDING is a real backend state (written before the provider
      // call), not an alias of SEND_PENDING. Render the clock for it here so
      // bubbles can pass the state through untouched instead of disguising
      // SENDING as SEND_PENDING before calling this function.
      case MessageState.SEND_PENDING:
      case MessageState.SENDING:
        return (
          <div>
            <ClockIcon className="w-5 h-4 text-neutral-content" />
          </div>
        );
      case MessageState.SENT:
        return (
          <div className="text-slate-500">
            <CheckIcon className="w-5 h-4" />
          </div>
        );
      case MessageState.DELIVERED:
        return (
          <div className="flex text-slate-500">
            <CheckIcon className="w-5 h-4" />
            <CheckIcon className="w-5 h-4 -ml-4" />
          </div>
        );
      case MessageState.READ:
        return (
          <div className="flex text-blue-400">
            <CheckIcon className="w-5 h-4" />
            <CheckIcon className="w-5 h-4 -ml-4" />
          </div>
        );
      case MessageState.ERROR: {
        // BUG-704 (P0): 3063/3064 are informational notes, not errors.
        // showCheckStatus only renders for fromMe events, so an echo-path
        // (business-authored) edit would otherwise show a pulsing red error
        // icon — violating the non-error requirement. Mirror the
        // WAPI_HELD_FOR_QUALITY_ERROR carve-out below, but render NO status
        // icon: the note itself lives in the bubble body (MessageByContentType).
        if (
          error === MessageErrorType.WAPI_EDITED_MESSAGE_ERROR ||
          error === MessageErrorType.WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR
        ) {
          return '';
        }

        const tooltipMessage = isWapi
          ? renderMessageNotSupported(error ?? '') ||
            t('messageError.defaultError')
          : showErrorMessage(error ?? '') || t('messageError.defaultError');

        if (error === MessageErrorType.WAPI_HELD_FOR_QUALITY_ERROR) {
          return (
            <Tooltip position="left" message={tooltipMessage}>
              <ClockIcon className="w-5 h-5 text-warning" />
            </Tooltip>
          );
        }
        // INT-1503: no pulse. A WhatsApp API error Meta refused shows its
        // notice inside the bubble and never reaches this icon; the rest keep
        // the static icon and its tooltip.
        return (
          <Tooltip
            position="left"
            {...(isWapi ? { color: 'error' as const } : {})}
            message={tooltipMessage}
          >
            <span className="relative flex h-5 w-5 cursor-pointer">
              <ExclamationCircleIcon className="relative w-5 h-5 text-error" />
            </span>
          </Tooltip>
        );
      }
      default:
        return '';
    }
  }

  async function highlightMessages(eventId: string, data: tUpdateEventData) {
    try {
      if (
        !currentUserId ||
        !currentWorkspaceId ||
        !selectedChat ||
        !chatEvents
      ) {
        return;
      }

      setLoading(true);

      await ChatService.updateEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
          data: data,
        },
        currentUserId,
      );

      const updateEvents: tChatEvent[] = chatEvents.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            message: {
              ...event.message,
              error: MessageErrorType.NO_ERROR,
              isFavorite: data.is_favorite || false,
              isForwarded: event.message?.isForwarded || false,
              fromMe: event.message?.fromMe || false,
              participant: {
                handle: event.message?.participant.handle || '',
                name: event.message?.participant.name || '',
              },
              state: event.message?.state || MessageState.SENT,
            },
          };
        }
        return event;
      });

      let filteredMessages;

      if (data.is_favorite) {
        const newEventFilter = updateEvents.filter(
          (item) => item.id === eventId,
        );
        filteredMessages =
          favoriteMessages && newEventFilter.concat(favoriteMessages);
      } else {
        filteredMessages =
          favoriteMessages &&
          favoriteMessages?.filter((item) => item.id !== eventId);
      }

      setFavoriteMessages(filteredMessages);
      setChatEvents(updateEvents);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `${error}`,
      });
    } finally {
      setLoading(false);
    }
  }

  async function deleteMessage(eventId: string, channelType: string) {
    try {
      if (!currentWorkspaceId || !currentUser || !chatEvents) {
        return;
      }
      setLoading(true);
      await ChatService.deleteChatEvent(eventId, {
        channel_type: channelType,
        auth_id: currentUser.authId,
        workspace_id: currentWorkspaceId,
      });
      setLoading(false);
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messageError.deleteSuccess'),
      });

      const updatedEvents = chatEvents.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            type: EventType.OUTBOUND_DELETED,
          };
        }
        return event;
      });

      setChatEvents(updatedEvents);
    } catch (error) {
      // Deleting for everyone is an outbound action, so whatsapp-web-service
      // refuses it while the plan is paused. The toast used to paste the error
      // object itself ("AxiosError: Request failed with status code 423").
      const refusal = lookupOutboundRefusal(error);
      addAlert(
        refusal
          ? buildOutboundRefusalAlert(refusal, t)
          : { type: eAlertType.ERROR, message: t('messageError.deleteError') },
      );
      // Without this the delete spinner kept spinning after a failed delete.
      setLoading(false);
    }
  }

  function showErrorMessage(error: MessageErrorType | string) {
    switch (error) {
      case MessageErrorType.PROVIDER_SEND_ERROR:
        return t('messageError.providerSendError');
      case MessageErrorType.UNKNOWN_ERROR_FROM_PROVIDER:
        return t('messageError.unknownError');
      case MessageErrorType.MESSAGE_NOT_SUPPORTED:
        return t('messageError.messageNotSupported');
      case MessageErrorType.MESSAGE_DECRYPTION_ERROR:
        return t('messageError.decryptionError');
      case MessageErrorType.MESSAGE_NOT_FOUND_ON_PROVIDER:
        return t('messageError.messageNotFoundOnProvider');
      case MessageErrorType.MESSAGE_REJECTED_BY_PROVIDER:
        return t('messageError.messageRejectedByProvider');
      case MessageErrorType.ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP:
        return t('messageError.recipientNotFoundOnWhatsapp');
      case MessageErrorType.MESSAGE_WINDOW_CLOSED:
        return t('messageError.messageWindowClosed');
      // BUG-865: WhatsApp Web has no 24h window. A dead session used to reach
      // this switch as MESSAGE_WINDOW_CLOSED and render the line above, which
      // sent both the client and support chasing a window that cannot exist.
      case MessageErrorType.WHATSAPP_WEB_ACCOUNT_DISCONNECTED:
        return t('messageError.whatsappWebAccountDisconnected');
      // BUG-827: WhatsApp limited this line's new chats, through the new-chat
      // quota or a reach-out timelock after bulk sending. It used to land on
      // PROVIDER_SEND_ERROR, whose copy blames the connection and pushes the
      // user to reconnect -- which burns tcTokens and fixes nothing.
      case MessageErrorType.WHATSAPP_WEB_NEW_CHAT_LIMIT_REACHED:
        return t('messageError.whatsappWebNewChatLimitReached');
      // INT-898: Meta refused the send because the lead has been silent for
      // more than 7 days. Distinct from MESSAGE_WINDOW_CLOSED (1009, the 24h
      // window): here the template path is closed too, so the only remedy is
      // reaching the customer from the Messenger/Instagram app itself.
      case MessageErrorType.META_INACTIVITY_WINDOW_EXPIRED:
        return t('messageError.metaInactivityWindowExpired');
      // INT-1377: Meta never told us whether this one was delivered, so the
      // copy has to send the seller to look at the chat. A plain "it failed"
      // reads as "send it again", and on a photo whose caption was the only
      // half that dropped that puts the message in front of the lead twice.
      case MessageErrorType.META_SEND_UNCONFIRMED:
        return t('messageError.metaSendUnconfirmed');
      // BUG-908 U12: same shape as 5002 one channel over. The retry was
      // claimed and we never learned whether its dispatch left, so the copy
      // has to send the advisor to look rather than invite another send --
      // and this code is deliberately absent from every retry allowlist, so
      // no button appears beside it.
      case MessageErrorType.WAPI_RETRY_UNCONFIRMED:
        return t('messageError.wapiRetryUnconfirmed');
      // INT-1437: each of these names the cause and what to do about it. The
      // one thing none of them may say is that the lead is unreachable, which
      // is what all of them used to say.
      case MessageErrorType.META_MESSAGE_TOO_LONG:
        return t('messageError.metaMessageTooLong');
      case MessageErrorType.META_ATTACHMENT_UNSUPPORTED:
        return t('messageError.metaAttachmentUnsupported');
      case MessageErrorType.META_ATTACHMENT_UPLOAD_FAILED:
        return t('messageError.metaAttachmentUploadFailed');
      case MessageErrorType.META_THREAD_CONTROL_LOST:
        return t('messageError.metaThreadControlLost');
      // No retry in this copy on purpose: Meta cannot read the file off our
      // media host, so sending it again fails identically. The fix is ours.
      case MessageErrorType.META_ATTACHMENT_FETCH_FAILED:
        return t('messageError.metaAttachmentFetchFailed');
      // No number in this copy: Meta's size cap varies by media type, and its
      // own message warns that encoding can inflate the uploaded size.
      case MessageErrorType.META_ATTACHMENT_TOO_LARGE:
        return t('messageError.metaAttachmentTooLarge');
      case MessageErrorType.RECIPIENT_UNAVAILABLE:
        return t('messageError.recipientUnavailable');
      case MessageErrorType.AUTH_INVALID_TOKEN_ERROR:
        return t('messageError.authInvalidToken');
      case MessageErrorType.MEDIA_UPLOAD_ERROR:
        return t('messageError.mediaUploadError');
      case MessageErrorType.EMPTY_MESSAGE_ERROR:
      case MessageErrorType.EMPTY_MESSAGE:
        return t('messageError.emptyMessage');
      case MessageErrorType.RECIPIENT_NOT_FOUND_ON_PROVIDER:
        return t('messageError.recipientNotFoundOnProvider');
      case MessageErrorType.TIKTOK_PROVIDER_REJECTED:
        return t('messageError.tiktokProviderRejected');
      case MessageErrorType.TIKTOK_UNSUPPORTED_ATTACHMENT_TYPE:
        return t('messageError.tiktokUnsupportedAttachment');
      case MessageErrorType.TIKTOK_IMAGE_NOT_SUPPORTED:
        return t('messageError.tiktokImageNotSupported');
      case MessageErrorType.TIKTOK_MESSAGE_WINDOW_RESTRICTED:
        return t('messageError.tiktokMessageWindowRestricted');
      case MessageErrorType.WORKSPACE_PLAN_PAUSED:
      case MessageErrorType.SENDER_WORKSPACE_MISMATCH:
      case MessageErrorType.SENDER_WORKSPACE_UNRESOLVABLE:
        return outboundRefusalLine(error);
      default:
        return '';
    }
  }

  function renderMessageNotSupported(error: string, fromMe?: boolean) {
    switch (error) {
      // BUG-704: the 131051 edit subtype (3063) renders a direction-aware,
      // non-error note. Inbound (lead-authored) adds the resend ask; echo-path
      // (business-authored, fromMe) drops it — telling the seller to ask the
      // contact to resend text the contact never wrote would be wrong.
      case MessageErrorType.WAPI_EDITED_MESSAGE_ERROR:
        return fromMe
          ? t('messageNotSupported.wapiEditedMessageOutbound')
          : t('messageNotSupported.wapiEditedMessageInbound');
      // 3064: generic minor subtype, honest note with actor-appropriate phrasing.
      case MessageErrorType.WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR:
        return fromMe
          ? t('messageNotSupported.wapiGenericUnsupportedMessageOutbound')
          : t('messageNotSupported.wapiGenericUnsupportedMessageInbound');
      // INT-1704: what the contact sent reads as the contact's (the inbound
      // notice in the bubble body). Only an explicit `fromMe === false` is
      // inbound: the tooltip passes no direction and only renders for fromMe.
      // 1004 has no case: it takes the INT-1690 default below.
      case MessageErrorType.PINNED_MESSAGE_NOTIFICATION_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.pinnedMessageNotificationInbound')
          : t('messageNotSupported.pinnedMessageNotificationNotSupported');
      case MessageErrorType.PLACEHOLDER_MESSAGE_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.placeholderMessageInbound')
          : t('messageNotSupported.placeholderMessageNotSupported');
      case MessageErrorType.TEMPLATE_MESSAGE_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.templateMessageInbound')
          : t('messageNotSupported.templateMessageNotSupported');
      case MessageErrorType.INTERACTIVE_MESSAGE_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.interactiveMessageInbound')
          : t('messageNotSupported.interactiveMessageNotSupported');
      case MessageErrorType.LOTTIE_STICKER_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.lottieStickerInbound')
          : t('messageNotSupported.lottieStickerNotSupported');
      case MessageErrorType.GROUP_MENTION_IN_STATUS_NOTIFICATION_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.groupMentionInStatusNotificationInbound')
          : t(
              'messageNotSupported.groupMentionInStatusNotificationNotSupported',
            );
      case MessageErrorType.GROUP_INVITE_MESSAGE_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.groupInviteMessageInbound')
          : t('messageNotSupported.groupInviteMessageNotSupported');
      case MessageErrorType.DEVICE_SENT_MESSAGE_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.deviceSentMessageInbound')
          : t('messageNotSupported.deviceSentMessageNotSupported');
      case MessageErrorType.COMMUNITY_ANNOUNCEMENT_COMMENT_NOT_SUPPORTED:
        return fromMe === false
          ? t('messageNotSupported.communityAnnouncementCommentInbound')
          : t('messageNotSupported.communityAnnouncementCommentNotSupported');
      case MessageErrorType.WAPI_CREDIT_CARD_EXPIRED_ERROR:
        return t('messageNotSupported.wapiCreditCardExpiredError');
      case MessageErrorType.WAPI_API_UNKNOWN_ERROR:
        return t('messageNotSupported.wapiApiUnknownError');
      case MessageErrorType.WAPI_SERVICE_UNAVAILABLE_ERROR:
        return t('messageNotSupported.wapiServiceUnavailableError');
      // BUG-587: role-routed — the remedy lives in Business Manager.
      case MessageErrorType.WAPI_INVALID_PARAM_VALUE_ERROR:
        return roleVariant('messageNotSupported.wapiInvalidParamValueError');
      // Deliberately NOT role-routed. Meta 100 is a generic "unsupported or
      // misspelled parameter" bucket: over-long text, a malformed value, a raw
      // WhatsApp Web JID in `to`, an empty body. Only one of its documented
      // sub-cases (a phone-number-id mismatch while *registering* a number) is
      // an admin task, and that one cannot arise from a chat send. The rest are
      // fixed by whoever composed the message, so there is no admin/seller split
      // to make — and pointing an admin at Business Manager sent them hunting a
      // mismatch that did not exist.
      case MessageErrorType.WAPI_INVALID_PARAMETER_ERROR:
        return t('messageNotSupported.wapiInvalidParameterError');
      case MessageErrorType.WAPI_ACCESS_TOKEN_ERROR:
        return t('messageNotSupported.wapiAccessTokenError');
      case MessageErrorType.WAPI_PERMISSION_ERROR:
        return t('messageNotSupported.wapiPermissionError');
      case MessageErrorType.WAPI_BUSINESS_VERIFICATION_REQUIRED_ERROR:
        return t('messageNotSupported.wapiBusinessVerificationRequiredError');
      case MessageErrorType.WAPI_PHONE_NUMBER_VERIFICATION_REQUIRED_ERROR:
        return t(
          'messageNotSupported.wapiPhoneNumberVerificationRequiredError',
        );
      case MessageErrorType.WAPI_TEMPLATE_NOT_FOUND_ERROR:
        return t('messageNotSupported.wapiTemplateNotFoundError');
      case MessageErrorType.WAPI_TEMPORARILY_BLOCKED_ERROR:
        return t('messageNotSupported.wapiTemporarilyBlockedError');
      case MessageErrorType.WAPI_UNKNOWN_ERROR:
        return t('messageNotSupported.wapiUnknownError');
      case MessageErrorType.WAPI_GENERAL_FAILURE_ERROR:
        return t('messageNotSupported.wapiGeneralFailureError');
      case MessageErrorType.WAPI_ACCESS_DENIED_ERROR:
        return t('messageNotSupported.wapiAccessDeniedError');
      case MessageErrorType.WAPI_MISSING_PARAMETER_ERROR:
        return t('messageNotSupported.wapiMissingParameterError');
      case MessageErrorType.WAPI_SERVICE_UNAVAILABLE_ERROR_2:
        return t('messageNotSupported.wapiServiceUnavailableError2');
      case MessageErrorType.WAPI_RECIPIENT_SENDER_CONFLICT_ERROR:
        return t('messageNotSupported.wapiRecipientSenderConflictError');
      case MessageErrorType.WAPI_MESSAGE_UNDELIVERABLE_ERROR:
        return t('messageNotSupported.wapiMessageUndeliverableError');
      // BUG-587: role-routed — only an admin-side user can raise this with Meta.
      case MessageErrorType.WAPI_DISCONNECTED_ACCOUNT_ERROR:
        return roleVariant('messageNotSupported.wapiDisconnectedAccountError');
      case MessageErrorType.WAPI_DISPLAY_NAME_PENDING_ERROR:
        return t('messageNotSupported.wapiDisplayNamePendingError');
      // BUG-587: role-routed — billing is fixed in Business Manager.
      case MessageErrorType.WAPI_BUSINESS_ISSUE_ERROR:
        return roleVariant('messageNotSupported.wapiBusinessIssueError');
      case MessageErrorType.WAPI_CERTIFICATE_ERROR:
        return t('messageNotSupported.wapiCertificateError');
      case MessageErrorType.WAPI_REENGAGEMENT_REQUIRED_ERROR:
        return t('messageNotSupported.wapiReengagementRequiredError');
      case MessageErrorType.WAPI_RATE_LIMITED_ERROR:
        return t('messageNotSupported.wapiRateLimitedError');
      // INT-1214: local throughput-limiter rejection (INT-1205) — distinct
      // copy from WAPI_RATE_LIMITED_ERROR, whose "marked as spam" wording
      // would mislead: this send never reached Meta and a retry works.
      case MessageErrorType.WAPI_SEND_RATE_LIMITED_ERROR:
        return t('messageNotSupported.wapiSendRateLimitedError');
      case MessageErrorType.WAPI_HEALTHY_ENGAGEMENT_ERROR:
        return t('messageNotSupported.wapiHealthyEngagementError');
      case MessageErrorType.WAPI_USER_STOPPED_MARKETING_ERROR:
        return t('messageNotSupported.wapiUserStoppedMarketingError');
      case MessageErrorType.WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR:
        return t('messageNotSupported.wapiUnsupportedMessageTypeError');
      case MessageErrorType.WAPI_MEDIA_DOWNLOAD_ERROR:
        return t('messageNotSupported.wapiMediaDownloadError');
      case MessageErrorType.WAPI_MEDIA_UPLOAD_ERROR:
        return t('messageNotSupported.wapiMediaUploadError');
      case MessageErrorType.WAPI_PAIR_RATE_LIMIT_ERROR:
        return t('messageNotSupported.wapiPairRateLimitError');
      case MessageErrorType.WAPI_CONCURRENCY_LIMIT_ERROR:
        return t('messageNotSupported.wapiConcurrencyLimitError');
      case MessageErrorType.WAPI_TEMPLATE_PARAM_COUNT_ERROR:
        return t('messageNotSupported.wapiTemplateParamCountError');
      case MessageErrorType.WAPI_TEMPLATE_NOT_APPROVED_ERROR:
        return t('messageNotSupported.wapiTemplateNotApprovedError');
      case MessageErrorType.WAPI_HYDRATED_TEXT_TOO_LONG_ERROR:
        return t('messageNotSupported.wapiHydratedTextTooLongError');
      case MessageErrorType.WAPI_FORMAT_POLICY_VIOLATION_ERROR:
        return t('messageNotSupported.wapiFormatPolicyViolationError');
      case MessageErrorType.WAPI_PARAMETER_FORMAT_MISMATCH_ERROR:
        return t('messageNotSupported.wapiParameterFormatMismatchError');
      case MessageErrorType.WAPI_TEMPLATE_PAUSED_ERROR:
        return t('messageNotSupported.wapiTemplatePausedError');
      case MessageErrorType.WAPI_TEMPLATE_DISABLED_ERROR:
        return t('messageNotSupported.wapiTemplateDisabledError');
      case MessageErrorType.WAPI_TEMPLATE_INVALID_PARAMS_ERROR:
        return t('messageNotSupported.wapiTemplateInvalidParamsError');
      case MessageErrorType.WAPI_INCOMPLETE_DEREGISTRATION_ERROR:
        return t('messageNotSupported.wapiIncompleteDeregistrationError');
      case MessageErrorType.WAPI_REGISTRATION_SERVER_UNAVAILABLE_ERROR:
        return t('messageNotSupported.wapiRegistrationServerUnavailableError');
      case MessageErrorType.WAPI_DUPLICATE_LANGUAGE_CONTENT_ERROR:
        return t('messageNotSupported.wapiDuplicateLanguageContentError');
      case MessageErrorType.WAPI_TEMPLATE_LIMIT_EXCEEDED_ERROR:
        return t('messageNotSupported.wapiTemplateLimitExceededError');
      case MessageErrorType.WAPI_CHARACTER_LIMIT_EXCEEDED_ERROR:
        return t('messageNotSupported.wapiCharacterLimitExceededError');
      case MessageErrorType.WAPI_MISSING_FIELDS_ERROR:
        return t('messageNotSupported.wapiMissingFieldsError');
      case MessageErrorType.WAPI_HEADER_FORMAT_ERROR:
        return t('messageNotSupported.wapiHeaderFormatError');
      case MessageErrorType.WAPI_BODY_FORMAT_ERROR:
        return t('messageNotSupported.wapiBodyFormatError');
      case MessageErrorType.WAPI_FOOTER_FORMAT_ERROR:
        return t('messageNotSupported.wapiFooterFormatError');
      case MessageErrorType.WAPI_TEMPLATE_RECENTLY_DELETED_ERROR:
        return t('messageNotSupported.wapiTemplateRecentlyDeletedError');
      case MessageErrorType.WAPI_UNSUPPORTED_MESSAGE_ERROR:
        return t('messageNotSupported.wapiUnsupportedMessage');
      case MessageErrorType.WAPI_CLOUD_API_NOT_AVAILABLE_ERROR:
        return t('messageNotSupported.wapiCloudApiNotAvailableError');
      case MessageErrorType.WAPI_HELD_FOR_QUALITY_ERROR:
        return t('messageNotSupported.wapiHeldForQualityError');
      // BUG-548: 11 errors that used to fall through to the generic
      // `messageNotSupported.emptyMessage` default. Each now has its own
      // actionable copy. The cases live alphabetically grouped by domain
      // (auth → rate-limit → recipient → template) for legibility.
      case MessageErrorType.WAPI_AUTH_EXCEPTION_ERROR:
        return t('messageNotSupported.wapiAuthExceptionError');
      case MessageErrorType.WAPI_API_METHOD_ERROR:
        return t('messageNotSupported.wapiApiMethodError');
      case MessageErrorType.WAPI_PERMISSION_DENIED_ERROR:
        return t('messageNotSupported.wapiPermissionDeniedError');
      case MessageErrorType.WAPI_ACCOUNT_RATE_LIMIT_ERROR:
        return t('messageNotSupported.wapiAccountRateLimitError');
      case MessageErrorType.WAPI_RATE_LIMIT_HIT_ERROR:
        return t('messageNotSupported.wapiRateLimitHitError');
      case MessageErrorType.WAPI_BUSINESS_BLOCKED_USER_ERROR:
        return t('messageNotSupported.wapiBusinessBlockedUserError');
      case MessageErrorType.WAPI_RECIPIENT_EXPERIMENT_ERROR:
        return t('messageNotSupported.wapiRecipientExperimentError');
      case MessageErrorType.WAPI_MESSAGING_RESTRICTED_COUNTRY_ERROR:
        return t('messageNotSupported.wapiMessagingRestrictedCountryError');
      case MessageErrorType.WAPI_RECIPIENT_NOT_ALLOWED_ERROR:
        return t('messageNotSupported.wapiRecipientNotAllowedError');
      case MessageErrorType.WAPI_TEMPLATE_WORD_RATIO_ERROR:
        return t('messageNotSupported.wapiTemplateWordRatioError');
      case MessageErrorType.WAPI_TEMPLATE_LEADING_TRAILING_PARAM_ERROR:
        return t('messageNotSupported.wapiTemplateLeadingTrailingParamError');
      // The WhatsApp API tooltip reads this switch, not showErrorMessage, so a
      // refused send needs its line here too or it falls to "Meta cannot
      // display this message", which blames Meta for our own refusal.
      case MessageErrorType.WORKSPACE_PLAN_PAUSED:
      case MessageErrorType.SENDER_WORKSPACE_MISMATCH:
      case MessageErrorType.SENDER_WORKSPACE_UNRESOLVABLE:
        return outboundRefusalLine(error);
      // BUG-548 mitigation: when the upstream chain emits a code that this
      // front-end (or any of the backends) has not yet learned, fall back to
      // a less alarming copy than `emptyMessage`'s "Meta no puede visualizar
      // este mensaje" — that string implies a Meta-side block when in reality
      // we just don't have a translation yet.
      case MessageErrorType.UNKNOWN_ERROR_FROM_PROVIDER:
        return t('messageNotSupported.wapiUnknownProviderError');
      // INT-1690: "we could not send" is wrong on a message the contact sent.
      // Only an explicit `fromMe === false` reads as inbound: the tooltip
      // caller passes no direction and only renders for outbound events.
      default:
        return fromMe === false
          ? t('messageNotSupported.inboundEmptyMessage')
          : t('messageNotSupported.emptyMessage');
    }
  }

  return {
    showCheckStatus,
    highlightMessages,
    loading,
    setLoading,
    deleteMessage,
    showErrorMessage,
    renderMessageNotSupported,
  };
}
